import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import {
    useDrawer,
    useFormAnalytics,
    useNotification,
    useUser,
} from "@hmn/coolinarika-web-core/hooks";

import { Button, ButtonsRow, buttonVariants } from "../../../Button";
import { DefaultFallback, withErrorBoundary } from "../../../ErrorBoundary";
import { DynamicForm, getFormInitialValues, getFormValidation } from "../../../Form";
import { CheckMarkIcon, Icon } from "../../../Icon";
import { drawerUID, notificationUID } from "../../uids";
import styles from "./Forms.style";

const { LOGIN: LOGIN_DRAWER_UID } = drawerUID;
const { NEW_PASSWORD: NEW_PASSWORD_NOTIFICATION_UID } = notificationUID;

const AuthFormNewPasswordStyled = styled.div((props) => ({ ...styles(props) }));

// Form inputs
const inputs = [
    {
        columns: [
            {
                layout: "form.column.auth",
                type: "password",
                id: "password",
                name: "password",
                autoComplete: "new-password",
                placeholder: "Nova lozinka",
                validationType: "string",
                validations: [
                    {
                        type: "required",
                    },
                ],
            },
            {
                layout: "form.column.auth",
                type: "password",
                id: "repeatPassword",
                name: "repeatPassword",
                autoComplete: "new-password",
                placeholder: "Ponovi novu lozinku",
                validationType: "string",
                validations: [
                    {
                        type: "required",
                    },
                ],
            },
        ],
    },
];

const AuthFormNewPassword = ({ className, ...rest }) => {
    const dispatch = useDispatch();
    const { query } = useRouter();
    const { validatePasswordResetToken, newPassword } = useUser();
    const [validToken, setValidToken] = useState(false);

    const { eventWithTrackingData } = useFormAnalytics({
        trackingData: {
            elementLabel: "New password",
        },
    });

    const { sendNotification } = useNotification({
        id: NEW_PASSWORD_NOTIFICATION_UID,
        options: { type: "error", duration: 15000 },
    });

    const { open: openLoginDrawer } = useDrawer(LOGIN_DRAWER_UID);

    useEffect(() => {
        const passwordResetToken = query?.["zaboravljena-lozinka"];

        const validateToken = async () => {
            const tokenData = await validatePasswordResetToken({ token: passwordResetToken });
            if (tokenData && tokenData?.isTokenValid) {
                setValidToken(passwordResetToken);
            } else {
                sendNotification({ message: "Neispravan token.", type: "error" });
            }
        };

        validateToken();
    }, [query, validatePasswordResetToken]);

    // Handle submit
    const onSubmit = useCallback(
        async (values) => {
            const { password } = values;
            await newPassword({ token: validToken, password });
            sendNotification({ message: "Lozinka promijenjena.", type: "success" });
            eventWithTrackingData();
            setTimeout(() => {
                openLoginDrawer();
            }, 3500);
        },
        [dispatch, validToken, eventWithTrackingData],
    );

    return (
        <AuthFormNewPasswordStyled className={className} {...rest}>
            <Formik
                initialValues={getFormInitialValues(inputs)}
                validationSchema={getFormValidation(inputs)}
                onSubmit={onSubmit}
            >
                {({ isSubmitting }) => (
                    <Form className="u-formFullHeight" noValidate>
                        <DynamicForm fields={inputs} />
                        <ButtonsRow>
                            <Button
                                title="Promijeni lozinku"
                                type="submit"
                                width="35%"
                                variant={buttonVariants.QUATERNARY}
                                iconRightComponent={<Icon icon={CheckMarkIcon} />}
                                disabled={!validToken || isSubmitting}
                            >
                                Promijeni lozinku
                            </Button>
                        </ButtonsRow>
                    </Form>
                )}
            </Formik>
        </AuthFormNewPasswordStyled>
    );
};

AuthFormNewPassword.propTypes = {
    className: PropTypes.string,
};

AuthFormNewPassword.defaultProps = {
    className: undefined,
};

export default withErrorBoundary(AuthFormNewPassword, {
    FallbackComponent: (err) => <DefaultFallback err={err} />,
});
